import {
  CocinaInsumoAgotado,
  CocinaItem,
  CocinaItemDespachado,
  CocinaPedido,
  EstadoItem,
  Pagina,
  formatearDuracion,
} from '../models/cocina.model';
import {
  DispatchedItemDto,
  ItemActionDto,
  PageDto,
  PrepStatusDto,
  SoldOutItemDto,
  TicketDto,
  TicketItemDto,
} from './kitchen-api.types';

const ESTADO_ITEM: Record<PrepStatusDto, EstadoItem> = {
  PENDING: 'espera',
  IN_PROGRESS: 'preparar',
  READY: 'despachado',
  DELIVERED: 'despachado',
  CANCELLED: 'agotado',
};

/**
 * Accion del API para el estado elegido en la pantalla. "Despachado" es
 * listo (READY) salvo con despacho directo, que lo da por entregado.
 * "espera" no es una accion: la pantalla no devuelve items a la cola.
 */
export function accionPara(estado: EstadoItem, despachoDirecto: boolean): ItemActionDto | null {
  switch (estado) {
    case 'preparar':
      return 'IN_PROGRESS';
    case 'despachado':
      return despachoDirecto ? 'DELIVERED' : 'READY';
    case 'agotado':
      return 'SOLD_OUT';
    default:
      return null;
  }
}

/** ahoraMs se inyecta para no depender del reloj en los tests. */
export function dtoAPedido(dto: TicketDto, ahoraMs: number = Date.now()): CocinaPedido {
  return {
    id: dto.ticketId,
    ordenId: dto.orderId,
    ordenNumero: dto.orderNumber,
    estacion: dto.station,
    mesaNumero: dto.venuePointId,
    esHabitacion: dto.venuePointType === 'ROOM',
    meseroNombre: dto.waiterName,
    clienteNombre: dto.customerName,
    estado: dto.status === 'PENDING' ? 'espera' : 'preparacion',
    // Se ancla al reloj del navegador para no depender de que ambos relojes coincidan
    inicioMs: ahoraMs - (dto.elapsedSeconds ?? 0) * 1000,
    items: dto.items.map(dtoAItem),
  };
}

function dtoAItem(dto: TicketItemDto): CocinaItem {
  return {
    id: dto.orderItemId,
    productoId: dto.productId,
    nombre: dto.productName,
    cantidad: dto.quantity,
    complementos: (dto.modifiers ?? []).map((m) => ({ nombre: m.name, cantidad: m.quantity ?? 1 })),
    notas: dto.notes,
    estado: ESTADO_ITEM[dto.status],
    paraLlevar: dto.toGo === true,
    retrasado: dto.delayed,
  };
}

export function dtoADespachado(dto: DispatchedItemDto): CocinaItemDespachado {
  let mesa = 'Pedido';
  if (dto.venuePointId) {
    mesa = `${dto.venuePointType === 'ROOM' ? 'Habitación' : 'Mesa'}: ${dto.venuePointId}`;
  } else if (dto.customerName) {
    mesa = dto.customerName;
  }
  return {
    id: dto.orderItemId,
    mesa,
    mesero: dto.waiterName ?? '-',
    pedido: dto.productName,
    cantidad: dto.quantity,
    horaPedido: hora(dto.orderedAt),
    horaDespacho: hora(dto.dispatchedAt),
    tiempoEspera: formatearDuracion(dto.waitSeconds),
    notas: dto.notes ?? '-',
  };
}

/** Tipos de insumo de inventario. */
const TIPO_INSUMO: Record<string, string> = {
  RAW_MATERIAL: 'Materia prima',
  DISPOSABLE: 'Descartable',
  CONSUMABLE: 'Consumible',
};

export function dtoAAgotado(dto: SoldOutItemDto): CocinaInsumoAgotado {
  return {
    id: dto.inventoryItemId,
    insumo: dto.name,
    sku: dto.sku ?? '-',
    tipo: TIPO_INSUMO[dto.itemType ?? ''] ?? dto.itemType ?? '-',
    categoria: dto.category ?? 'Sin categoría',
    disponible: dto.availableQuantity ?? 0,
  };
}

export function dtoAPagina<D, M>(dto: PageDto<D>, mapear: (item: D) => M): Pagina<M> {
  return {
    items: dto.content.map(mapear),
    pagina: dto.page,
    tamano: dto.size,
    total: dto.totalElements,
    totalPaginas: dto.totalPages,
  };
}

/** "2026-09-24T08:10:05.123" -> "08:10:05". */
function hora(fechaHora: string | null): string {
  if (!fechaHora) {
    return '-';
  }
  const indice = fechaHora.indexOf('T');
  return indice >= 0 ? fechaHora.slice(indice + 1, indice + 9) : fechaHora;
}
